import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { createDashboard, deleteDashboard, listDashboards, updateDashboard } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDateShort } from "../lib/format";
import { PageBar } from "../components/Layout";
import { Modal } from "../components/Modal";
import { confirmDialog } from "../components/ConfirmDialog";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import type { DashboardRow } from "../lib/types";

const DASHBOARD_SORT: Record<string, SortAccessor<DashboardRow>> = {
  name: (d) => d.name,
  created: (d) => d.created_at,
};

export function DashboardsPage() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi((signal) => listDashboards(signal), []);
  const dashboards = data?.dashboards ?? [];
  const { rows: sortedDashboards, sort, toggle } = useTableSort(dashboards, DASHBOARD_SORT);
  // null = closed, "new" = create, row = rename
  const [editing, setEditing] = useState<DashboardRow | "new" | null>(null);

  return (
    <>
      <PageBar
        crumbs="Dashboards"
        actions={
          <button className="btn primary" onClick={() => setEditing("new")}>
            + New dashboard
          </button>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Dashboards</h1>
            <div className="sub">Shareable widget canvases aggregated across your work.</div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>Dashboards</h3>
            <span className="faint">{dashboards.length}</span>
          </div>
          <div className="card-body">
            {loading ? (
              <Loading label="Loading dashboards…" />
            ) : error ? (
              <ErrorState error={error} onRetry={reload} />
            ) : dashboards.length === 0 ? (
              <EmptyState>No dashboards yet.</EmptyState>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
                    <SortTh field="created" sort={sort} onSort={toggle}>Created</SortTh>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {sortedDashboards.map((d) => (
                    <DashboardTr
                      key={d.id}
                      dashboard={d}
                      onOpen={() => navigate(`/dashboards/${d.id}`)}
                      onEdit={() => setEditing(d)}
                      onChanged={reload}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {editing ? (
        <DashboardModal
          dashboard={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(created) => {
            setEditing(null);
            reload();
            if (created) navigate(`/dashboards/${created}`);
          }}
        />
      ) : null}
    </>
  );
}

/** One dashboard row — click to open; Edit (rename) + Delete to match the
 *  approved list-table format. */
function DashboardTr({
  dashboard,
  onOpen,
  onEdit,
  onChanged,
}: {
  dashboard: DashboardRow;
  onOpen: () => void;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const deleteMut = useMutation(deleteDashboard);
  const busy = deleteMut.pending;

  return (
    <tr
      className="clickable"
      onClick={onOpen}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
    >
      <td>
        <strong>{dashboard.name}</strong>
      </td>
      <td className="num">{formatDateShort(dashboard.created_at)}</td>
      <td className="row-actions">
        <button
          className="btn small"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          Edit
        </button>
        <button
          className="btn small danger"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            void confirmDialog({ message: `Delete dashboard "${dashboard.name}"?`, danger: true }).then((yes) => {
              if (yes) void deleteMut.run(dashboard.id).then((ok) => ok && onChanged());
            });
          }}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

/** Add/rename dashboard modal — null dashboard = create. */
function DashboardModal({
  dashboard,
  onClose,
  onSaved,
}: {
  dashboard: DashboardRow | null;
  onClose: () => void;
  onSaved: (createdId?: string) => void;
}) {
  const [name, setName] = useState(dashboard?.name ?? "");
  const createMut = useMutation(createDashboard);
  const updateMut = useMutation((id: string, n: string) => updateDashboard(id, n));
  const pending = createMut.pending || updateMut.pending;
  const error = createMut.error || updateMut.error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    if (dashboard) {
      const res = await updateMut.run(dashboard.id, name.trim());
      if (res) onSaved();
    } else {
      const res = await createMut.run(name.trim());
      if (res) onSaved(res.dashboard.id);
    }
  }

  return (
    <Modal
      title={dashboard ? "Rename dashboard" : "New dashboard"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="dashboard-form" disabled={pending || !name.trim()}>
            {pending ? <Spinner label="Saving…" /> : dashboard ? "Save changes" : "Create dashboard"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="dashboard-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Support overview"
            autoFocus
            required
          />
        </label>
      </form>
    </Modal>
  );
}
